import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { toast } from 'react-toastify';
import '../styles/Modal.css';

interface TypeDemandeModalProps {
  show: boolean;
  handleClose: () => void;
  onTypesUpdated?: (newTypes: string[]) => void; // Callback avec les nouveaux types
  currentTypes?: string[]; // Types actuels passés depuis la page parente
  collection?: string; // Collection Firestore à utiliser (default: "companies")
}

interface TypeDemande {
  name: string;
}

const TypeDemandeModal: React.FC<TypeDemandeModalProps> = ({ show, handleClose, onTypesUpdated, currentTypes, collection = "companies" }) => {
  const [newType, setNewType] = useState<TypeDemande>({
    name: ''
  });
  const [existingTypes, setExistingTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Utiliser les types passés depuis la page parente
  useEffect(() => {
    if (show && currentTypes) {
      console.log("Types reçus dans la modale depuis la page parente:", currentTypes);
      setExistingTypes(currentTypes);
    } else if (!show) {
      // Réinitialiser quand la modale se ferme
      setExistingTypes([]);
      setNewType({ name: '' });
    }
  }, [show, currentTypes]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newType.name.trim()) {
      toast.error("Le nom du type de demande est obligatoire");
      return;
    }

    if (existingTypes.includes(newType.name.trim())) {
      toast.error("Ce type de demande existe déjà");
      return;
    }

    setLoading(true);
    
        try {
      const docRef = doc(db, "prospects", collection);
      
      const newTypeName = newType.name.trim();
      
      // Ajouter le nouveau type à la liste
      await updateDoc(docRef, {
        opportunityTypes: arrayUnion(newTypeName)
      });

       toast.success("Type de demande ajouté avec succès !");
       
       // Réinitialiser le formulaire
       setNewType({
         name: ''
       });
       
       // Mettre à jour la liste locale immédiatement
       const updatedTypes = [...existingTypes, newTypeName].sort();
       setExistingTypes(updatedTypes);
       
       // Notifier la page parente du changement avec les nouveaux types
       if (onTypesUpdated) {
         console.log("Appel du callback onTypesUpdated depuis la modale avec les types:", updatedTypes);
         onTypesUpdated(updatedTypes);
       }
      
    } catch (error) {
      console.error("Erreur lors de l'ajout du type:", error);
      toast.error("Erreur lors de l'ajout du type de demande");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TypeDemande, value: string) => {
    setNewType(prev => ({
      ...prev,
      [field]: value
    }));
  };

    const handleDeleteType = async (typeToDelete: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le type "${typeToDelete}" ?`)) {
      return;
    }

    setLoading(true);
    
    try {
      const docRef = doc(db, "prospects", collection);
      
      // Utiliser les types actuels de la modale
      const updatedTypes = existingTypes.filter((type: string) => type !== typeToDelete);
      
      await updateDoc(docRef, {
        opportunityTypes: updatedTypes
      });
      
      toast.success(`Type "${typeToDelete}" supprimé avec succès !`);
      
      // Mettre à jour la liste locale immédiatement
      setExistingTypes(updatedTypes);
      
      // Notifier la page parente du changement avec les nouveaux types
      if (onTypesUpdated) {
        console.log("Appel du callback onTypesUpdated depuis la modale (suppression) avec les types:", updatedTypes);
        onTypesUpdated(updatedTypes);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du type:", error);
      toast.error("Erreur lors de la suppression du type");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header">
          <h2>Ajouter un type de demande</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
                         <div className="form-group">
               <label htmlFor="typeName">Nom du type de demande *</label>
               <input
                 type="text"
                 id="typeName"
                 value={newType.name}
                 onChange={(e) => handleInputChange('name', e.target.value)}
                 placeholder="Ex: Enregistrement FDA (USA)"
                 required
                 style={{ width: '100%', padding: '8px', marginTop: '5px' }}
               />
             </div>

                         {existingTypes.length > 0 && (
               <div className="form-group">
                 <label>Types existants :</label>
                 <div style={{ 
                   maxHeight: '200px', 
                   overflowY: 'auto', 
                   border: '1px solid #ddd', 
                   borderRadius: '4px', 
                   padding: '15px',
                   marginTop: '5px',
                   backgroundColor: '#f9f9f9',
                   display: 'flex',
                   flexWrap: 'wrap',
                   gap: '8px'
                 }}>
                   {existingTypes.map((type, index) => (
                     <div key={index} style={{ 
                       display: 'flex',
                       alignItems: 'center',
                       gap: '6px',
                       padding: '6px 10px', 
                       backgroundColor: '#424272', 
                       color: 'white',
                       borderRadius: '20px',
                       fontSize: '0.85em',
                       fontWeight: '500',
                       boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                       cursor: 'pointer',
                       transition: 'all 0.2s ease'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.backgroundColor = '#dc3545';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.backgroundColor = '#424272';
                     }}
                     onClick={() => handleDeleteType(type)}
                     title={`Cliquer pour supprimer "${type}"`}
                     >
                       <span>{type}</span>
                       <span style={{ 
                         fontSize: '1.1em', 
                         fontWeight: 'bold',
                         lineHeight: '1'
                       }}>×</span>
                     </div>
                   ))}
                 </div>
                 <p style={{ 
                   fontSize: '0.8em', 
                   color: '#666', 
                   marginTop: '8px',
                   fontStyle: 'italic'
                 }}>
                   Cliquez sur un tag pour le supprimer
                 </p>
               </div>
             )}
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={handleClose}
              className="cancel-button"
              disabled={loading}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={loading}
              style={{ backgroundColor: '#424272' }}
            >
              {loading ? 'Ajout en cours...' : 'Ajouter le type'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TypeDemandeModal;
